import type { CrawlerType, StatAdvancement, CharacterOrigin } from './character.ts';
import type { StatBlock, StatId } from './stats.ts';
import type { HotlistEntry } from '../rules/hotlist.ts';

/**
 * A described change to a Character.
 *
 * ONE closed union covering every AUDITED, REVERSIBLE change to the sheet.
 * Creation, advancement, the Tutorial award, play (damage, Mana, marks) and
 * manual editing all express themselves as these, so for that family there is
 * exactly one place where legality is checked and one place where the sheet is
 * written.
 *
 * **IT IS NOT EVERY CHANGE, and saying so was inaccurate.** Priced GAMEPLAY
 * mutations - the free weapon swap (E3), drinking a potion (E4), the Ready /
 * Swap Action (E5) - build the next Character in a pure rules module and
 * persist it through `persistMutation` alongside the next Runtime. They write
 * NO entry here, deliberately: each spends an Action, and an Undo that restored
 * the potion without refunding the Action would be a misleading half-reversal
 * of a transaction this log cannot describe. See `docs/ARCHITECTURE.md`.
 *
 * Adding a member is deliberate work: it needs a rule in
 * `src/rules/characterChanges.ts` and an inverse so the change can be undone.
 * This is not a scripting language.
 */
export type CharacterChange =
  | { kind: 'set-name'; name: string }
  | { kind: 'set-crawler-type'; crawlerType: CrawlerType }
  | { kind: 'set-size'; size: number }
  | { kind: 'set-stats'; stats: StatBlock }
  | { kind: 'set-stat-advancement'; advancement: StatAdvancement }
  | { kind: 'set-stats-and-advancement'; stats: StatBlock; advancement: StatAdvancement }
  | { kind: 'set-stat'; stat: StatId; value: number }
  | { kind: 'add-skill'; skillId: string; rank: number }
  | { kind: 'remove-skill'; skillId: string }
  | { kind: 'set-skill-rank'; skillId: string; rank: number }
  | { kind: 'adjust-skill-rank'; skillId: string; delta: number }
  | { kind: 'set-level'; level: number }
  | { kind: 'adjust-level'; delta: number }
  | { kind: 'set-floor'; floor: number }
  | { kind: 'add-advancement-mark'; skillId: string }
  | { kind: 'clear-advancement-marks'; skillIds: string[] }
  | { kind: 'set-health-bar-slots'; slotsRemaining: number }
  | { kind: 'set-current-mana'; currentMana: number }
  | { kind: 'set-ai-favor'; aiFavor: number }
  | { kind: 'set-dying-rounds'; dyingRoundsRemaining: number | null }
  | { kind: 'set-notes'; notes: string }
  | { kind: 'set-origin'; origin: CharacterOrigin | null }
  /**
   * Inventory. RULE DCC-INV-002 - `equipped` is mechanical state, so it is part
   * of what a change alters and part of what an inverse must restore.
   *
   * `set-item` replaces an entry WHOLE - quantity and equipped together -
   * rather than there being separate quantity and equip changes that could
   * leave the two disagreeing. `remove-item` takes the entry away entirely.
   */
  | { kind: 'add-item'; itemId: string; quantity: number }
  /**
   * RULE DCC-INV-018 - carries ALL FOUR parts of an entry, including `held`.
   * An inverse that dropped it would silently empty the crawler's hands, which
   * is the `remove-skill` lesson applied to physical possessions.
   */
  | { kind: 'set-item'; itemId: string; quantity: number; equipped: number; held: number }
  | { kind: 'remove-item'; itemId: string }
  /**
   * Hotlist configuration. RULE DCC-HOT-001.
   *
   * TWO members, not three. `set-hotlist-slot` with `entry: null` IS the clear
   * operation, exactly as `set-item` replaces an entry whole rather than there
   * being separate quantity and equip changes that could disagree. A dedicated
   * `clear` would be a third thing to keep correct for no new capability.
   *
   * `swap-hotlist-slots` covers both reordering and moving into an empty slot,
   * and is its own inverse. Order carries no mechanical meaning in the source
   * (recorded silence), so this is presentation the player controls.
   */
  | { kind: 'set-hotlist-slot'; slot: number; entry: HotlistEntry | null }
  | { kind: 'swap-hotlist-slots'; from: number; to: number };

/** Which system asked for the change, for the audit trail. */
export type ChangeSource =
  | 'creation'
  | 'advancement'
  | 'session-award'
  | 'play'
  | 'manual-edit';

export interface CharacterChangeRequest {
  change: CharacterChange;
  source: ChangeSource;
  /** Short human reason, e.g. "2-hour checkpoint". */
  reason: string;
}

/**
 * A change that was actually applied, with the changes that would undo it.
 * The inverse is computed from the values the character held BEFORE, so an
 * edit can be reviewed and reversed without re-deriving anything.
 *
 * `undo` is a LIST, and applied in order.
 *
 * It has to be. Some changes touch more than one field: removing a Skill also
 * drops its advancement mark, so restoring it needs `add-skill` AND
 * `add-advancement-mark`. A single inverse could not express that, and the
 * "exact reversal" guarantee was quietly untrue for those cases before this.
 *
 * An empty list is legitimate and means the change was a no-op: applying it
 * altered nothing, so reversing it must alter nothing either.
 *
 * Schema note: this was a single `CharacterChange` at character schema 5.
 * Migration 5 -> 6 wraps the old value in a one-element list.
 */
export interface AppliedChange extends CharacterChangeRequest {
  at: string;
  undo: CharacterChange[];
}

/**
 * Optional display names, so a review screen can say "Stealth" where the audit
 * log says "stealth". The domain layer has no content package of its own, so
 * the caller supplies the lookup. Everything still falls back to the stable ID.
 */
export interface ChangeNaming {
  skillName?: (skillId: string) => string;
}

/** Plain-language description, for review screens and the audit log. */
export function describeChange(change: CharacterChange, naming?: ChangeNaming): string {
  const skill = (id: string): string => naming?.skillName?.(id) ?? id;
  switch (change.kind) {
    case 'set-name':
      return `Name set to "${change.name}"`;
    case 'set-crawler-type':
      return `Crawler type set to ${change.crawlerType}`;
    case 'set-size':
      return `Size set to ${change.size}`;
    case 'set-stats':
      return 'All five Stats set';
    case 'set-stats-and-advancement':
      return 'Stat Points allocated; Stats and point balance saved together';
    case 'set-stat-advancement':
      return `Stat Points: ${change.advancement.unspentPoints} unspent`;
    case 'set-stat':
      return `${change.stat.toUpperCase()} set to ${change.value}`;
    case 'add-skill':
      return `Gained ${skill(change.skillId)} at Rank ${change.rank}`;
    case 'remove-skill':
      return `Removed ${skill(change.skillId)}`;
    case 'set-skill-rank':
      return `${skill(change.skillId)} set to Rank ${change.rank}`;
    case 'adjust-skill-rank':
      return `${skill(change.skillId)} Rank ${change.delta >= 0 ? '+' : ''}${change.delta}`;
    case 'set-level':
      return `Level set to ${change.level}`;
    case 'adjust-level':
      return `Level ${change.delta >= 0 ? '+' : ''}${change.delta}`;
    case 'set-floor':
      return `Floor set to ${change.floor}`;
    case 'add-advancement-mark':
      return `${skill(change.skillId)} marked for advancement`;
    case 'clear-advancement-marks':
      return `Cleared ${change.skillIds.length} advancement mark(s)`;
    case 'set-health-bar-slots':
      return `Health Bar set to ${change.slotsRemaining} slots`;
    case 'set-current-mana':
      return `Mana set to ${change.currentMana}`;
    case 'set-ai-favor':
      return `AI Favor set to ${change.aiFavor}`;
    case 'set-dying-rounds':
      return change.dyingRoundsRemaining === null
        ? 'No longer Dying'
        : `Dying: ${change.dyingRoundsRemaining} rounds left`;
    case 'set-notes':
      return 'Notes edited';
    case 'add-item':
      return `Gained ${change.quantity} x ${skill(change.itemId)}`;
    case 'set-item': {
      const where = [
        change.equipped > 0 ? `${change.equipped} equipped` : null,
        change.held > 0 ? `${change.held} held` : null,
      ].filter((part) => part !== null);
      return where.length > 0
        ? `${skill(change.itemId)} set to ${change.quantity} (${where.join(', ')})`
        : `${skill(change.itemId)} set to ${change.quantity}`;
    }
    case 'remove-item':
      return `Lost all ${skill(change.itemId)}`;
    case 'set-origin':
      return change.origin === null ? 'Creation record cleared' : 'Creation record set';
    case 'set-hotlist-slot':
      return change.entry === null
        ? `Hotlist slot ${change.slot + 1} cleared`
        : `Hotlist slot ${change.slot + 1} set to ${skill(
            change.entry.kind === 'item' ? change.entry.itemId : change.entry.skillId,
          )}`;
    case 'swap-hotlist-slots':
      return `Hotlist slots ${change.from + 1} and ${change.to + 1} swapped`;
  }
}
